import React, { useEffect } from 'react';
import { Form, Input, Modal, Select, InputNumber } from 'antd';

const { Option } = Select;

/**
 * GL Group Form - Senior Dev Practice:
 * - Decoupled UI and logic
 * - Clean prop validation (assumed via TypeScript or documentation)
 * - Ant Design Form best practices
 */
const GlGroupForm = ({ visible, onCreate, onCancel, record, glGroups = [] }) => {
  const [form] = Form.useForm();

  // Handle record changes and modal visibility for reset logic
  useEffect(() => {
    if (visible) {
      if (record) {
        // Edit mode: populate with record data
        form.setFieldsValue({
          name: record.name,
          parentId: record.parentId,
          ordinalIndex: record.ordinalIndex,
        });
      } else {
        // Create mode: explicit reset to defaults
        form.resetFields();
        form.setFieldsValue({
          name: '',
          parentId: 0,
          ordinalIndex: 1,
        });
      }
    }
  }, [record, form, visible]);

  // Clean-up reset for parent handler
  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(values => {
        onCreate(values);
        form.resetFields();
      })
      .catch(info => {
        console.error('Validation Failed:', info);
      });
  };

  return (
    <Modal
      visible={visible} // Use 'open' for newer antd versions, but keeping 'visible' for compatibility
      title={record ? "Update GL Group" : "Add GL Group"}
      okText="Submit"
      cancelText="Cancel"
      onCancel={handleCancel}
      onOk={handleOk}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        name="gl_group_form"
      >
        <Form.Item
          name="name"
          label="GL Group Name"
          rules={[
            { required: true, message: 'Please enter the GL group name!' },
            { min: 2, message: 'Name must be at least 2 characters long' }
          ]}
        >
          <Input placeholder="Enter name" />
        </Form.Item>

        <Form.Item
          name="parentId"
          label="Parent Group"
          rules={[{ required: true, message: 'Please select a parent group!' }]}
        >
          <Select
            placeholder="Select parent group"
            showSearch
            optionFilterProp="children"
            onChange={(value) => {
              // Senior Dev Practice: Dynamic field dependency handling
              const siblings = glGroups.filter(g => g.parentId === value);
              const maxOrdinal = siblings.reduce((max, curr) => Math.max(max, curr.ordinalIndex || 0), 0);

              // Propose next available index
              form.setFieldsValue({
                ordinalIndex: maxOrdinal + 1
              });
            }}
          >
            <Option value={0}>Root (Primary Group)</Option>
            {glGroups
              .filter(g => !record || g.id !== record.id) // Prevent self-parenting
              .map(group => (
                <Option key={group.id} value={group.id}>
                  {group.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="ordinalIndex"
          label="Sort Order (Ordinal Index)"
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 1" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GlGroupForm;